import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import styles from "./verifyPhone.module.scss";
import { profileApi } from "src/services/settings/profile";

function VerifyPhone() {
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get("token") as string;

  const [status, setStatus] = useState<"loading" | "success" | "error" | "expired" | "already_used" | "already_associated">("loading");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage("Invalid verification link");
      return;
    }

    let cancelled = false;

    async function verifyPhoneNumber() {
      try {
        const response = await profileApi.confirmSmsVerification(token);
        if (cancelled) return;

        setStatus("success");
        setMessage(response.message || "Your phone number has been successfully verified!");
      } catch (error: unknown) {
        if (cancelled) return;

        console.error("Verification error:", error);
        const err = error as {
          response?: { data?: { errors?: Array<{ code?: string; message?: string }>; message?: string } };
          errors?: Array<{ code?: string; message?: string }>;
        };
        // Handle both response.data.errors and root-level errors array
        const firstError = err.response?.data?.errors?.[0] || err.errors?.[0];
        const errorCode = firstError?.code || "";
        const errorMessage =
          firstError?.message ||
          err.response?.data?.message ||
          "Verification failed. Please try again.";

        // Check for expired link
        if (errorCode === "phone.verification_expired" || errorCode === "token.expired") {
          setStatus("expired");
          setMessage("This verification link has expired.");
        } 
        // Check for already used link (newer link was issued)
        else if (errorCode === "phone.verification_already_used") {
          setStatus("already_used");
          setMessage("A newer verification link has already been issued.");
        }
        // Check for phone number already associated with another user
        else if (errorCode === "phone.number_already_verified" || errorCode === "phone.number_already_assigned") {
          setStatus("already_associated");
          setMessage("This phone number is already associated with an existing user.");
        }
        // Check for invalid token
        else if (errorCode === "token.invalid" || errorCode === "resource.not_found") {
          setStatus("already_used");
          setMessage("A newer verification link has already been issued.");
        }
        // Generic error
        else {
          setStatus("error");
          setMessage(errorMessage);
        }
      }
    }

    verifyPhoneNumber();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const renderContent = () => {
    switch (status) {
      case "loading":
        return (
          <>
            <div className={styles.illustrationContainer}>
              <img src="/images/coaching/mobile_verify.svg" alt="Verifying phone" className={styles.illustration} />
            </div>
            <h1 className={styles.title}>Verifying your mobile number...</h1>
            <div className={styles.subtitleWithSpinner}>
              <div className={styles.smallSpinner}></div>
              <p className={styles.subtitle}>Please wait while we verify your phone number</p>
            </div>
          </>
        );

      case "success":
        return (
          <>
            <div className={styles.illustrationContainer}>
              <img src="/images/coaching/mobile_verify.svg" alt="Phone verified" className={styles.illustration} />
            </div>
            <h1 className={styles.title}>Mobile number verified!</h1>
            <p className={styles.successSubtitle}>
              <i className="paperflite-tick-selected"></i>
              Your mobile number has been verified successfully
            </p>
          </>
        );

      case "expired":
        return (
          <>
            <div className={styles.illustrationContainer}>
              <img
                src="/images/coaching/mobile_verify_error.svg"
                alt="Link expired"
                className={styles.illustration}
              />
            </div>
            <h1 className={styles.title}>Link Expired</h1>
            <p className={styles.expiredSubtitle}>
              <i className="paperflite-danger"></i>
              {message}
            </p>
            <p className={styles.actionMessage}>
              Please request a new verification link to continue.
            </p>
          </>
        );

      case "already_used":
        return (
          <>
            <div className={styles.illustrationContainer}>
              <img
                src="/images/coaching/mobile_verify_error.svg"
                alt="Link invalid"
                className={styles.illustration}
              />
            </div>
            <h1 className={styles.title}>Verification Link Invalid</h1>
            <p className={styles.infoSubtitle}>
              <i className="paperflite-info"></i>
              {message}
            </p>
            <p className={styles.actionMessage}>
              Please use the latest verification link sent to your phone.
            </p>
          </>
        );

      case "already_associated":
        return (
          <>
            <div className={styles.illustrationContainer}>
              <img
                src="/images/coaching/mobile_verify_error.svg"
                alt="Verification failed"
                className={styles.illustration}
              />
            </div>
            <h1 className={styles.title}>Verification Failed</h1>
            <p className={styles.errorSubtitle}>
              <i className="paperflite-danger"></i>
              {message}
            </p>
            <p className={styles.actionMessage}>
              Please use a different phone number or contact support.
            </p>
          </>
        );

      case "error":
        return (
          <>
            <div className={styles.illustrationContainer}>
              <img
                src="/images/coaching/mobile_verify_error.svg"
                alt="Verification failed"
                className={styles.illustration}
              />
            </div>
            <h1 className={styles.title}>Verification failed!</h1>
            <p className={styles.errorSubtitle}>
              <i className="paperflite-danger"></i>
              {message || "Verification failed. Please try again."}
            </p>
          </>
        );
    }
  };

  return (
    <div className={styles.verifyContainer}>
      <div className={styles.content}>
        {renderContent()}
        <div className={styles.logoContainer}>
          <img src="/images/Paperflite.png" alt="Paperflite Logo" className={styles.logo} />
        </div>
      </div>
    </div>
  );
}

export default VerifyPhone;
